import { CloseOutlined } from '@ant-design/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { ConnectionKind } from '@mm/state/model/ConnectionModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useConnectionWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './FacebookAdsIntegrationEditorSlideout.module.css';
import { FacebookAdsAudienceMapping } from './FacebookAdsAudienceMapping';
import { Button, Heading, Status } from '@mm/components';
import { Radio } from 'antd';
import { useAdAudienceCRUDActions } from '@mm/state/actions/AdAudienceCRUDActions';
import TextInput from '../../../../../common/components/TextInput';
import { selectAdAudience } from '@mm/state/selectors/adAudiencesSelector';
import AdAudienceModel from '@mm/state/model/AdAudienceModel';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import moment from 'moment/moment';
import { colors } from '@mm/utils';
import { SendDataConfig } from '@mm/state/model/WidgetModel';
import { selectWidgetFacebookAudienceConnections } from '@mm/state/selectors/connectionsSelectors';

export interface FacebookAdsIntegrationEditorSlideoutProps {
  widgetId?: number;
  adAudienceId?: number;
  adAudienceRemoteId?: number;
}

const renderAdAudienceStatus = (adAudience: AdAudienceModel) => {
  if (adAudience.synchronizationStatus === 'not_synchronized') {
    return <Status status="Not Ready" color={colors.grayLight} />;
  }

  if (adAudience.synchronizationStatus === 'started') {
    return <Status status="Started" color={colors.greenLighter} />;
  }

  if (adAudience.lastSynchronizationTime) {
    return <Status status="Synchronized" color={colors.green} />;
  }
};

export const FacebookAdsIntegrationEditorSlideout = ({
  widgetId,
  adAudienceId: adAudienceIdProp,
  adAudienceRemoteId,
}: FacebookAdsIntegrationEditorSlideoutProps) => {
  const [connectionId, setConnectionId] = useState();
  const [adAudienceId, setAdAudienceId] = useState(adAudienceIdProp);

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const adAudience = useSelector((state: StoreState) =>
    selectAdAudience(state, adAudienceId),
  );

  const chatbotId = useSelector(activeChatbotIdSelector);
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === widgetId)!;

  const config = widget?.config as Partial<SendDataConfig>;

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const connections =
    useSelector(selectWidgetFacebookAudienceConnections) ?? [];

  const [createDisabled, setCreateDisabled] = useState(false);

  const adAudienceCRUDActions = useAdAudienceCRUDActions();

  const viewActions = useViewActions();
  const connectionCRUDActions = useConnectionCRUDActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const { retrieveWidgetConnections } = useConnectionCRUDActions();
  const connection = connections.find(
    c => c.id === config?.connectionWebhookId,
  );

  const { setRef } = useSlideoutOnClose();

  const onClose = async () => {
    viewActions.hideSlideoutMenu();
  };

  const handleStatusChange = (synchronizeAutomatically: boolean) => {
    if (adAudience) {
      adAudienceCRUDActions.updateAdAudienceByRemoteId(
        facebookPageId,
        adAudience.remoteId,
        {
          synchronizeAutomatically,
        },
      );
    }
  };

  const handleAudienceChange = (audienceId: number) => {
    if (adAudience) {
      adAudienceCRUDActions.updateAdAudienceByRemoteId(
        facebookPageId,
        adAudience.remoteId,
        {
          audienceId,
        },
      );
    }
  };

  const [adAudienceName, setAdAudienceName] = useState('');

  const handleSyncNow = async (aad: AdAudienceModel) => {
    try {
      const res = await adAudienceCRUDActions.syncAudienceByRemoteId(
        facebookPageId,
        aad.remoteId,
      );
      if (res.adAudience) {
        successToast('Synchronization successfully launched');
      }
    } catch (err) {
      errorToast('Can not start synchronization');
    }
  };

  const handleCreate = async () => {
    try {
      const res = await adAudienceCRUDActions.createAdAudience(facebookPageId, {
        adAudienceName,
        synchronizeAutomatically: !!widget,
        audienceId: undefined,
      });

      const id = res.adAudience.id;

      if (id) {
        // from automation
        if (widgetId && widget) {
          const newConnection = await connectionCRUDActions.create(
            activeFbPageId,
            {
              name: 'Connection',
              kind: ConnectionKind.WIDGET,
              description: '',
              integrationType: 'facebook_ads_audience',
              adAudienceId: id,
            },
          );
          setConnectionId(newConnection.id);

          if (newConnection) {
            const clonedWidget = {
              ...widget,
              config: {
                connectionWebhookId: newConnection.id,
              },
            };
            await connectionWidgetHandlers.update(clonedWidget);
            await retrieveWidgetConnections(
              activeFbPageId,
              'facebook_ads_audience',
            );
          }
          successToast(
            'Facebook audience created and will be synced automatically',
          );
          // from integrations
        } else {
          successToast(
            'Facebook audience created, select Customer.ai audience to sync from',
          );
        }

        setAdAudienceName('');
        setAdAudienceId(id);
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (adAudienceRemoteId) {
      adAudienceCRUDActions
        .retrieveAudienceByRemoteId(facebookPageId, adAudienceRemoteId, true)
        .catch(() => {
          errorToast(
            `Couldn't retrieve Facebook Ad Audience ${adAudienceRemoteId}`,
          );
        });
    }
  }, [adAudienceRemoteId]);

  if (!adAudience) {
    return (
      <SlideoutMenu className={styles.menu} setRef={setRef}>
        <div className={styles.headerContent}>
          <Heading className={styles.headerTitle} size={3}>
            Create Facebook Audience
          </Heading>

          <div className={styles.headerButtons}>
            <Button
              disabled={!adAudienceName.trim().length || !!adAudienceRemoteId}
              onClick={handleCreate}
            >
              Create
            </Button>
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
        </div>

        <SlideoutMenu.Content className={styles.content}>
          <div className={styles.container}>
            <TextInput
              placeholder="Enter Facebook Audience name"
              value={adAudienceName}
              onChange={e => setAdAudienceName(e.target.value)}
            />
          </div>
        </SlideoutMenu.Content>
      </SlideoutMenu>
    );
  }

  const isFromAutomation = !!(widgets && widgetId);

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <Heading className={styles.headerTitle} size={3}>
          Sync to Facebook Audience: "{adAudience.name}"
        </Heading>

        <div className={styles.headerButtons}>
          {!isFromAutomation && (
            <>
              <Button
                disabled={adAudience.synchronizeAutomatically}
                onClick={() => handleSyncNow(adAudience)}
              >
                Sync to Facebook now
              </Button>
            </>
          )}
          <CloseOutlined className={styles.closeIcon} onClick={onClose} />
        </div>
      </div>

      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.container}>
          <div className={styles.configArea}>
            {!isFromAutomation && (
              <FacebookAdsAudienceMapping
                chatbotId={chatbotId}
                selectedAudienceId={adAudience.audience?.id}
                selectedAdAudienceId={connection?.adAudienceId}
                onAudienceChange={handleAudienceChange}
              />
            )}
            <div className={styles.options}>
              <Radio
                checked={!adAudience.synchronizeAutomatically}
                disabled={isFromAutomation}
                onChange={e => {
                  handleStatusChange(!e.target.checked);
                }}
              >
                Manually
              </Radio>
              <Radio
                checked={adAudience.synchronizeAutomatically}
                onChange={e => {
                  handleStatusChange(e.target.checked);
                }}
              >
                Automatically
              </Radio>
            </div>
            <div className={styles.fbAudienceCard}>
              <div className={styles.cardField}>
                <Heading className={styles.cardTitle} size={5}>
                  Name
                </Heading>
                <span className={styles.cardValue}>{adAudience.name}</span>
              </div>

              <div className={styles.cardField}>
                <Heading className={styles.cardTitle} size={5}>
                  Created
                </Heading>
                <span className={styles.cardValue}>
                  {moment(adAudience.createdAt).format('MM/DD/YYYY HH:mm')}
                </span>
              </div>

              <div className={styles.cardField}>
                <Heading className={styles.cardTitle} size={5}>
                  Status
                </Heading>
                <span className={styles.cardValue}>
                  {renderAdAudienceStatus(adAudience)}
                </span>
              </div>

              {connection && (
                <div className={styles.cardField}>
                  <Heading className={styles.cardTitle} size={5}>
                    Webhook status
                  </Heading>
                  <span className={styles.cardValue}>
                    <Status
                      status={connection.active ? 'Active' : 'Not active'}
                      color={
                        connection.active ? colors.green : colors.grayLight
                      }
                    />
                  </span>
                </div>
              )}

              <div className={styles.cardField}>
                <Heading className={styles.cardTitle} size={5}>
                  Last Synchronization Time
                </Heading>
                <span className={styles.cardValue}>
                  {adAudience.lastSynchronizationTime
                    ? moment(adAudience.lastSynchronizationTime).format(
                        'MM/DD/YYY HH:mm',
                      )
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
